import { useEffect, useRef, useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useMe } from "../hooks/useMe.tsx";
import { DEV_AUTH, signOut } from "../lib/auth.ts";
import { useThemeMode, type ThemeMode } from "../lib/themeMode.ts";
import { Icon } from "./Icons.tsx";
import { SearchBox } from "./SearchBox.tsx";
import { DetailPanel } from "./DetailPanel.tsx";
import { useFreshness, useSupply } from "../lib/supply.ts";
import { LangSwitch, t } from "../lib/i18n.tsx";
import { FACTORY_PAGES } from "../lib/roles.ts";

// Shell styles live in styles.css (.shell, .sidebar, .sb-*, .topbar):
// they need hover, focus and a narrow-screen drawer, which inline styles can't do.

type NavItem = { to: string; label: string; icon: (p: { size?: number }) => JSX.Element; sub?: string; badge?: "order" | "late" };
// Everyday work first; planning pages after; help last.
const NAV_GROUPS: { title: string; items: NavItem[] }[] = [
  {
    title: "Every day",
    items: [
      { to: "/", label: "Home", icon: Icon.Home },
      { to: "/order", label: "To order", icon: Icon.Cart, sub: "for buying", badge: "order" },
      { to: "/arriving", label: "Arriving", icon: Icon.Truck, sub: "for receiving", badge: "late" },
    ],
  },
  {
    title: "Planning",
    items: [
      { to: "/forecast", label: "Forecast", icon: Icon.Forecast },
      { to: "/trends", label: "Trends", icon: Icon.Trends, sub: "what customers choose" },
      { to: "/demand", label: "Yearly Demand", icon: Icon.Demand },
      { to: "/accuracy", label: "Forecast accuracy", icon: Icon.Accuracy },
    ],
  },
  {
    title: "Help",
    items: [
      { to: "/guide", label: "How this site works", icon: Icon.Guide },
      { to: "/overview", label: "Data status", icon: Icon.Dashboard },
    ],
  },
];
const ADMIN_ITEM = { to: "/admin", label: "Admin", icon: Icon.Admin };

const PAGE_TITLES: Record<string, string> = {
  "/": "Home",
  "/order": "To order",
  "/arriving": "Arriving",
  "/search": "Search",
  "/overview": "Data status",
  "/forecast": "Forecast",
  "/trends": "Trends",
  "/demand": "Yearly Demand",
  "/accuracy": "Forecast accuracy",
  "/guide": "Guide: how the app works",
  "/admin": "Admin",
  "/password": "Change password",
};

const THEME_OPTIONS: { mode: ThemeMode; label: string; icon: (p: { size?: number }) => JSX.Element }[] = [
  { mode: "light", label: "Light", icon: Icon.Sun },
  { mode: "dark", label: "Dark", icon: Icon.Moon },
  { mode: "system", label: "Auto", icon: Icon.Laptop },
];

/** March mark: the diamond with the orange chevron used across the March apps. */
function MarchMark() {
  return (
    <svg width="20" height="20" viewBox="0 0 28 28" fill="none" aria-hidden="true">
      <path d="M14 5 L23 14 L14 23 L5 14 Z" stroke="#ffffff" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M11 9.5 L15.5 14 L11 18.5" stroke="var(--accent)" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  const me = useMe();
  const loc = useLocation();
  const isAdmin = me.data?.role === "admin";
  const factory = me.data?.role === "factory";
  const navGroups = factory
    ? NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter((i) => FACTORY_PAGES.has(i.to)) })).filter((g) => g.items.length)
    : NAV_GROUPS;
  const pageTitle = t(PAGE_TITLES[loc.pathname] ?? "");
  const [navOpen, setNavOpen] = useState(false);
  const [mode, setMode] = useThemeMode();
  // Sticky table headers sit under the sticky top bar; its height changes on phones.
  const topbarRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = topbarRef.current;
    if (!el) return;
    const set = () => document.documentElement.style.setProperty("--topbar-h", `${el.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const supply = useSupply();
  const fresh = useFreshness();
  const badge = (b?: NavItem["badge"]) =>
    supply.loading || !b ? undefined : b === "order" ? String(supply.orderNow.length) : supply.late.length + supply.stalled.length ? t("{n} overdue", { n: supply.late.length + supply.stalled.length }) : undefined;

  // Close the drawer after navigating, and on Escape.
  useEffect(() => setNavOpen(false), [loc.pathname]);
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setNavOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navOpen]);

  return (
    <div className={`shell${navOpen ? " nav-open" : ""}`}>
      <aside className="sidebar" id="sidebar" aria-label={t("Main")}>
        <NavLink to="/" className="sb-brand">
          <span className="sb-logo">
            <MarchMark />
          </span>
          <span className="sb-name">
            March Inventory
            <small>March Products · Jurupa + Texas</small>
          </span>
        </NavLink>

        <nav className="sb-nav">
          {navGroups.map((g) => (
            <div key={g.title} style={{ display: "contents" }}>
              <div className="sb-group">{t(g.title)}</div>
              {g.items.map((item) => (
                <SideLink key={item.to} to={item.to} label={t(item.label)} sub={item.sub && t(item.sub)} badge={factory ? undefined : badge(item.badge)}>
                  <item.icon size={16} />
                </SideLink>
              ))}
            </div>
          ))}
          {isAdmin && (
            <>
              <div className="sb-group">{t("Admin")}</div>
              <SideLink to={ADMIN_ITEM.to} label={t(ADMIN_ITEM.label)}>
                <ADMIN_ITEM.icon size={16} />
              </SideLink>
            </>
          )}
        </nav>

        <div className="sb-foot">
          <div className="theme-switch" role="group" aria-label={t("Colour theme")}>
            {THEME_OPTIONS.map((o) => (
              <button
                key={o.mode}
                type="button"
                aria-pressed={mode === o.mode}
                title={o.mode === "system" ? t("Follow your computer's setting") : t(`${o.label} theme`)}
                onClick={() => setMode(o.mode)}
              >
                <o.icon size={13} />
                {t(o.label)}
              </button>
            ))}
          </div>
          <div className="sb-user">
            <div className="sb-avatar" aria-hidden="true">
              {(me.data?.email ?? "?").slice(0, 1).toUpperCase()}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="sb-user-name">{me.data?.displayName ?? me.data?.email ?? "…"}</div>
              <div className="sb-user-meta">
                {me.data?.role ? t(me.data.role) : t("loading")}
                {me.data?.via === "password" && (
                  <>
                    {" · "}
                    <NavLink to="/password" className="sb-btn">{t("Change password")}</NavLink>
                  </>
                )}
                {!DEV_AUTH && (
                  <>
                    {" · "}
                    <button
                      type="button"
                      className="sb-btn"
                      onClick={async () => {
                        await signOut();
                        location.assign("/signed-out");
                      }}
                    >
                      {t("Sign out")}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </aside>
      <div className="scrim" onClick={() => setNavOpen(false)} aria-hidden="true" />

      <main className="main">
        <header className="topbar" ref={topbarRef}>
          <button
            type="button"
            className="menu-btn"
            aria-label={t("Open menu")}
            aria-controls="sidebar"
            aria-expanded={navOpen}
            onClick={() => setNavOpen(true)}
          >
            <Icon.Menu size={18} />
          </button>
          <h1>{pageTitle}</h1>
          {factory ? <span style={{ flex: 1 }} /> : <SearchBox />}
          <LangSwitch />
          <div className="fresh" title={t("Stock syncs from NetSuite every hour")}>
            <span className="dot" />
            {fresh ? fresh.label : "…"}
          </div>
        </header>

        <div className="content">{children}</div>
        {!factory && (
          <div className="m-search">
            <SearchBox />
          </div>
        )}
      </main>
      <DetailPanel />
    </div>
  );
}

function SideLink({ to, label, sub, badge, children }: { to: string; label: string; sub?: string; badge?: string; children: ReactNode }) {
  return (
    <NavLink to={to} end={to === "/"} className={({ isActive }) => `sb-link${isActive ? " active" : ""}`}>
      {children}
      <span className="sb-label">
        {label}
        {sub && <span className="sb-sub">{sub}</span>}
      </span>
      {badge && <span className="sb-badge">{badge}</span>}
    </NavLink>
  );
}
